import {
  Users,
  Settings,
  Home,
  CalendarDays,
  Inbox,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Activity,
  BarChart2,
  Shield,
  PlayCircle,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@/components/blocks/evo-sidebar';
import { CompanySelector } from '@/components/CompanySelector';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Portal as TooltipPortal } from '@radix-ui/react-tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TourHelpButton } from '@/components/TourHelpButton';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useCompany } from '@/contexts/CompanyContext';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { useMemo, useState, useEffect, useCallback } from 'react';
import type { Booking } from '@/types';

type PageName =
  | 'overview'
  | 'calendar'
  | 'heatmap'
  | 'resources'
  | 'projects'
  | 'settings'
  | 'unallocated'
  | 'dependent-fields'
  | 'report-utilisation'
  | 'report-utilisation-forecast'
  | 'report-available-capacity'
  | 'report-resource-allocation'
  | 'report-project-staffing'
  | 'report-bench'
  | 'report-audit-log'
  | 'roles-permissions'
  | 'roles-permissions-edit'
  | 'user-setup';

const reportPages: PageName[] = [
  'report-utilisation',
  'report-utilisation-forecast',
  'report-available-capacity',
  'report-resource-allocation',
  'report-project-staffing',
  'report-bench',
  'report-audit-log',
];

const reportSubItems: { id: PageName; label: string }[] = [
  { id: 'report-utilisation',          label: 'Utilisation report' },
  { id: 'report-utilisation-forecast', label: 'Utilisation forecast' },
  { id: 'report-available-capacity',   label: 'Available capacity' },
  { id: 'report-resource-allocation',  label: 'Resource allocation' },
  { id: 'report-project-staffing',     label: 'Project staffing' },
  { id: 'report-bench',                label: 'Bench report' },
  { id: 'report-audit-log',            label: 'Audit log' },
];

interface AppSidebarProps {
  currentPage: PageName;
  onNavigate: (page: PageName) => void;
  settingsTab?: string;
  onNavigateSettings?: (tab: string) => void;
}

const navItems = [
  {
    id: 'overview' as PageName,
    label: 'Homepage',
    icon: Home,
    tourId: undefined,
  },
  {
    id: 'calendar' as PageName,
    label: 'Planner',
    icon: CalendarDays,
    tourId: 'navigation',
  },
  {
    id: 'heatmap' as PageName,
    label: 'Heatmap',
    icon: Activity,
    tourId: undefined,
  },
  {
    id: 'unallocated' as PageName,
    label: 'Open Demand',
    icon: Inbox,
    tourId: undefined,
  },
  {
    id: 'resources' as PageName,
    label: 'Resources',
    icon: Users,
    tourId: undefined,
  },
  {
    id: 'projects' as PageName,
    label: 'Projects',
    icon: FolderKanban,
    tourId: undefined,
  },
  // Reports is rendered separately as a collapsible below navItems
];

export function AppSidebar({ currentPage, onNavigate, settingsTab, onNavigateSettings }: AppSidebarProps) {
  const { state } = useSidebar();
  const { mockData, hasLiveData } = useScheduler();
  const { getScreenMode } = useDataMode();
  const { currentCompany } = useCompany();
  const currentMode = getScreenMode('calendar');
  const isLive = currentMode === 'live';
  const isCollapsed = state === 'collapsed';
  const [settingsOpen, setSettingsOpen] = useState(currentPage === 'settings');
  const isOnReportPage = reportPages.includes(currentPage);
  const [reportsOpen, setReportsOpen] = useState(isOnReportPage);
  const { canViewNavItem, canViewReport, canViewSettingsTab, canViewRolesPermissions, canViewResourceDirectory, hasFullAccess } = usePermissions();
  const { hasCompleted, serverStatus, openWizard } = useOnboarding();

  /**
   * Onboarding sidebar button visibility rules:
   * - notStarted  → show "Start Onboarding" (first entry point for admins)
   * - inProgress  → show "Resume Onboarding" (started but not yet completed)
   * - completed   → hidden (onboarding is done for this company)
   */
  const showOnboardingButton = hasFullAccess && serverStatus !== 'completed';
  const onboardingButtonLabel = serverStatus === 'inProgress' ? 'Resume Onboarding' : 'Start Onboarding';
  const handleOnboardingClick = openWizard;

  const settingsMenuItems = useMemo(() => {
    const items: { tab: string; label: string; page?: PageName; icon?: typeof Shield }[] = [
      { tab: 'settings', label: 'General' },
      { tab: 'user-setup', label: 'User Setup', page: 'user-setup' },
    ];
    if (canViewRolesPermissions()) {
      items.push({ tab: 'roles-permissions', label: 'Roles & Permissions', page: 'roles-permissions', icon: Shield });
    }
    if (canViewSettingsTab('viewTimezone')) {
      items.push({ tab: 'timezone', label: 'Timezone' });
    }
    if (canViewSettingsTab('viewBookingTypeSetup')) {
      items.push({ tab: 'booking-type-setup', label: 'Booking Type Setup' });
    }
    if (canViewSettingsTab('viewGroupSetup')) {
      items.push({ tab: 'group-setup', label: 'Planner Group Setup' });
    }
    items.push({ tab: 'integrations', label: 'Integration' });
    items.push({ tab: 'audit', label: 'Audit' });
    return items;
  }, [canViewRolesPermissions, canViewSettingsTab]);

  // In live mode, fetch unallocated bookings from the API (liveData.bookings
  // is never bulk-fetched — the calendar uses date-range queries instead).
  const [liveUnallocatedBookings, setLiveUnallocatedBookings] = useState<Booking[]>([]);

  const fetchLiveUnallocated = useCallback(() => {
    if (!isLive || !hasLiveData || !currentCompany?.id) {
      setLiveUnallocatedBookings([]);
      return;
    }
    let cancelled = false;
    bookingsApi.getUnallocated(false, currentCompany.id).then(apiBookings => {
      if (cancelled) return;
      setLiveUnallocatedBookings(apiBookings.map(ab => {
        const mapped = mapApiBookingToBooking(ab);
        if (ab.resourceId == null) {
          mapped.resourceId = null;
        }
        return mapped;
      }));
    }).catch(() => {
      if (!cancelled) setLiveUnallocatedBookings([]);
    });
    return () => { cancelled = true; };
  }, [isLive, hasLiveData, currentCompany?.id]);

  useEffect(() => {
    return fetchLiveUnallocated();
  }, [fetchLiveUnallocated]);

  const unallocatedStats = useMemo(() => {
    const bookings = isLive ? liveUnallocatedBookings : mockData.bookings;
    const unallocated = isLive
      ? bookings.filter(b => b.resourceId == null)
      : bookings.filter(b => b.resourceId === 'unallocated');
    const now = new Date();
    const in3Days = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const critical = unallocated.filter(b => new Date(b.startTime) <= in3Days).length;
    const soon = unallocated.filter(b => {
      const start = new Date(b.startTime);
      return start > in3Days && start <= in7Days;
    }).length;
    const upcoming = unallocated.filter(b => new Date(b.startTime) > in7Days).length;
    return { total: unallocated.length, critical, soon, upcoming };
  }, [isLive, liveUnallocatedBookings, mockData.bookings]);

  const badgeLabel = unallocatedStats.total > 20 ? '20+' : String(unallocatedStats.total);

  const badgeColour =
    unallocatedStats.critical > 0
      ? 'bg-destructive text-destructive-foreground'
      : unallocatedStats.soon > 0
      ? 'bg-yellow-500 text-white'
      : 'bg-primary text-primary-foreground';

  // Filter nav items by permission
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      switch (item.id) {
        case 'overview': return canViewNavItem('viewDashboard');
        case 'calendar': return true; // Always visible (core feature)
        case 'heatmap': return canViewNavItem('viewHeatmap');
        case 'unallocated': return canViewNavItem('viewOpenDemand');
        case 'resources': return canViewResourceDirectory();
        case 'projects': return canViewNavItem('viewProjects');
        default: return true;
      }
    });
  }, [canViewNavItem, canViewResourceDirectory]);

  // Filter report sub-items by permission
  const visibleReportSubItems = useMemo(() => {
    const map: Record<string, string> = {
      'report-utilisation': 'utilisationReport',
      'report-utilisation-forecast': 'utilisationForecastReport',
      'report-available-capacity': 'availableCapacityReport',
      'report-resource-allocation': 'resourceAllocationReport',
      'report-project-staffing': 'projectStaffingReport',
      'report-bench': 'benchReport',
      'report-audit-log': 'auditLogReport',
    };
    return reportSubItems.filter(({ id }) => {
      const reportKey = map[id];
      return reportKey ? canViewReport(reportKey) : true;
    });
  }, [canViewReport]);

  const hasAnyReports = visibleReportSubItems.length > 0;

  return (
    <Sidebar variant="floating" collapsible="icon">

      {/* ── Header: toggle trigger + app name ── */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden text-neutral-700">
            Scheduler
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* ── Main navigation ── */}
      <SidebarContent className="pt-2">
        {isCollapsed ? (
          /* ── Collapsed nav: plain flex column, bypasses w-fit chain entirely ── */
          <div className="flex flex-col items-center gap-1 w-full px-0 py-2">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              const isUnallocated = item.id === 'unallocated';

              if (isUnallocated) {
                return (
                  <TooltipProvider key={item.id} delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onNavigate(item.id)}
                          data-active={isActive}
                          className="relative flex size-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-teal-100 hover:text-teal-700 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground"
                          aria-label={item.label}
                        >
                          <Icon className="size-4 shrink-0" />
                          {unallocatedStats.total > 0 && (
                            <span className={`absolute top-0.5 right-0.5 h-2 w-2 rounded-full border border-sidebar ${
                              unallocatedStats.critical > 0 ? 'bg-destructive' : unallocatedStats.soon > 0 ? 'bg-yellow-500' : 'bg-primary'
                            }`} />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent side="right" className="text-xs space-y-1 p-3 min-w-[180px]">
                          <p className="font-semibold mb-1.5">
                            {unallocatedStats.total > 0 ? `${unallocatedStats.total} unresourced booking${unallocatedStats.total !== 1 ? 's' : ''}` : 'Open Demand'}
                          </p>
                          {unallocatedStats.critical > 0 && (
                            <p className="flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-destructive inline-block shrink-0" />
                              <span className="text-destructive font-medium">{unallocatedStats.critical} starting within 3 days</span>
                            </p>
                          )}
                          {unallocatedStats.soon > 0 && (
                            <p className="flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-yellow-500 inline-block shrink-0" />
                              <span className="text-yellow-600 dark:text-yellow-400 font-medium">{unallocatedStats.soon} starting within 7 days</span>
                            </p>
                          )}
                          {unallocatedStats.upcoming > 0 && (
                            <p className="flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-muted-foreground inline-block shrink-0" />
                              <span className="text-muted-foreground">{unallocatedStats.upcoming} upcoming</span>
                            </p>
                          )}
                          {unallocatedStats.total === 0 && <p className="text-muted-foreground">No unresourced bookings</p>}
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return (
                <TooltipProvider key={item.id} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => onNavigate(item.id)}
                        data-active={isActive}
                        data-tour={item.tourId}
                        className="flex size-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-teal-100 hover:text-teal-700 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground"
                        aria-label={item.label}
                      >
                        <Icon className="size-4 shrink-0" />
                      </button>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent side="right">{item.label}</TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              );
            })}

            {/* Reports collapsed icon */}
            {hasAnyReports && (
              <DropdownMenu>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-active={isOnReportPage}
                          className="flex size-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-teal-100 hover:text-teal-700 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground"
                          aria-label="Reports"
                        >
                          <BarChart2 className="size-4 shrink-0" />
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent side="right">Reports</TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent side="right" align="start" className="w-52 ml-1">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Reports</div>
                  {visibleReportSubItems.map(({ id, label }) => (
                    <DropdownMenuItem key={id} onClick={() => onNavigate(id)}
                      className={`cursor-pointer text-sm font-normal ${currentPage === id ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' : ''}`}>
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          /* ── Expanded nav ── */
          <SidebarGroup className="px-[17px] pt-0">
            <SidebarGroupContent>
              <SidebarMenu data-tour="navigation">
                {visibleNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;
                  const isUnallocated = item.id === 'unallocated';

                  if (isUnallocated) {
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => onNavigate(item.id)}
                          className="relative [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700"
                        >
                          <Icon />
                          <span>{item.label}</span>
                          {unallocatedStats.total > 0 && (
                            <SidebarMenuBadge className={`${isActive ? 'bg-background/20 text-inherit' : badgeColour} text-[10px] font-bold h-4 min-w-[18px] px-1`}>
                              {badgeLabel}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => onNavigate(item.id)}
                        data-tour={item.tourId}
                        className="[&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700"
                      >
                        <Icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}

                {/* Reports expanded collapsible */}
                {hasAnyReports && (
                  <Collapsible open={reportsOpen} onOpenChange={setReportsOpen}>
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton isActive={isOnReportPage}
                          className="[&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700">
                          <BarChart2 />
                          <span>Reports</span>
                          {reportsOpen
                            ? <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-teal-600" />
                            : <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-teal-600" />}
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                    </SidebarMenuItem>
                    <CollapsibleContent>
                      <div className="ml-[1.35rem] pl-3 border-l border-sidebar-border mt-1 mb-1 space-y-0.5">
                        {visibleReportSubItems.map(({ id, label }) => (
                          <SidebarMenuItem key={id}>
                            <SidebarMenuButton isActive={currentPage === id} onClick={() => onNavigate(id)}
                              className="justify-start text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700">
                              <span>{label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* ── Footer: Settings → Company → Help ── */}
      <SidebarFooter className={`gap-1 pb-3 pt-2 ${isCollapsed ? "px-0" : "px-2"}`}>
        {isCollapsed ? (
          /* ── Collapsed footer: plain flex column ── */
          <div className="flex flex-col items-center gap-1 w-full">
            {/* Settings icon */}
            <DropdownMenu>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-active={currentPage === 'settings' || currentPage === 'roles-permissions' || currentPage === 'roles-permissions-edit' || currentPage === 'user-setup'}
                        data-tour="settings"
                        className="flex size-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-teal-100 hover:text-teal-700 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground"
                        aria-label="Settings"
                      >
                        <Settings className="size-4 shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent side="right">Settings</TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent side="right" align="end" className="w-52 ml-1">
                {settingsMenuItems.map((item) => (
                  <DropdownMenuItem key={item.tab}
                    onClick={() => item.page ? onNavigate(item.page) : onNavigateSettings?.(item.tab)}
                    className={`cursor-pointer text-sm font-normal ${(item.page ? currentPage === item.page : currentPage === 'settings' && settingsTab === item.tab) ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' : ''}`}>
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <SidebarSeparator className="w-full mx-0" />

            <div className="flex justify-center w-full pt-1">
              <CompanySelector sidebarMode collapsed={true} />
            </div>

            {showOnboardingButton && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOnboardingClick}
                      className="flex size-8 items-center justify-center rounded-md text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                      aria-label={onboardingButtonLabel}
                    >
                      <PlayCircle className="size-4 shrink-0" />
                    </button>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent side="right"><p className="text-xs">{onboardingButtonLabel}</p></TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            )}

            <TourHelpButton />
          </div>
        ) : (
          /* ── Expanded footer ── */
          <>
            <SidebarMenu>
              <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton isActive={false} data-tour="settings"
                      className="[&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700">
                      <Settings />
                      <span>Settings</span>
                      {settingsOpen
                        ? <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-teal-600" />
                        : <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-teal-600" />}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-[1.35rem] pl-3 border-l border-sidebar-border mt-1 space-y-0.5">
                      {settingsMenuItems.map((item) => (
                        <SidebarMenuItem key={item.tab}>
                          <SidebarMenuButton
                            isActive={item.page ? currentPage === item.page : currentPage === 'settings' && settingsTab === item.tab}
                            onClick={() => item.page ? onNavigate(item.page) : onNavigateSettings?.(item.tab)}
                            className="justify-start text-sm font-medium text-foreground hover:bg-teal-100 hover:text-teal-700">
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </div>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>

            <SidebarSeparator />

            <div className="px-3 pt-2">
              <CompanySelector sidebarMode collapsed={false} />
            </div>

            {showOnboardingButton && (
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={handleOnboardingClick}
                    className="gap-2 [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50">
                    <PlayCircle />
                    <span>{onboardingButtonLabel}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            )}

            <SidebarMenu>
              <SidebarMenuItem>
                <TourHelpButton />
              </SidebarMenuItem>
            </SidebarMenu>
          </>
        )}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
